// src/utils/analytics.ts
//
// The single gateway for product analytics.
//
// Every product event goes through `track()`:
//   - `AnalyticsEvents` below is the complete event catalog as a TypeScript
//     type - an off-catalog event name or a missing/mistyped property is a
//     compile error, and the type doubles as the schema documentation;
//   - event names follow `category:object_action` (lowercase snake_case),
//     properties are snake_case; the category prefix is what groups events
//     in the PostHog UI (`event LIKE 'chat:%'` selects a whole category);
//   - ambient context (current agent, active tab) is merged into every event
//     so any event can be broken down by agent/tab without each call site
//     having to thread those values through.
//
// Direct use of posthog outside this module is lint-banned
// (no-restricted-imports); the exceptions are utils/posthog.ts (owns the SDK
// init) and api/utils/captureError.ts (owns posthog.captureException for
// Error tracking - $exception is deliberately NOT part of this catalog).

import posthog from "./posthog";

export { initPostHog as initAnalytics } from "./posthog";
// The raw client, exported ONLY for @posthog/react's <PostHogProvider client>.
// Never use it to capture events - that's what track() is for.
export { default as posthogClient } from "./posthog";

/**
 * Tab identifiers as users saw them.
 *
 * @deprecated The Analysis and Dashboard tabs were retired along with the tab
 * strip itself, so chat is now the task pane's only surface and this is
 * effectively a constant. "analysis" and "dashboard" are kept so existing
 * PostHog insights built on the historical events still typecheck against the
 * same schema.
 */
export type AnalyticsView = "chat" | "analysis" | "dashboard";

export type TokenRefreshTrigger = "timer" | "http_401" | "send_preflight" | "manual";

type ChatSettingName =
  | "extended_thinking"
  | "web_search"
  | "composer_mode"
  | "benchmark_mode"
  | "concise_mode";

/**
 * The event catalog. One entry per product event, keyed by the exact event
 * name sent to PostHog. Add new events here first - the compiler then guides
 * every call site.
 */
export interface AnalyticsEvents {
  // ── app: lifecycle and navigation ────────────────────────────────────────
  /** First successful sign-in of this page load ("user opened the add-in"). */
  "app:opened": {
    auth_tier: string;
    token_expires_at?: string;
    /** Legacy SSO retry attempt that finally succeeded (absent for NAA). */
    attempt?: number;
  };
  /**
   * @deprecated No longer emitted. The tab strip was removed, so there is only
   * one view to be in. Retained for the historical insights built on it.
   */
  "app:view_changed": {
    from: AnalyticsView;
    to: AnalyticsView;
    has_messages: boolean;
  };
  "app:went_offline": { offline_count: number };
  "app:back_online": { offline_count: number; offline_duration_ms: number | null };
  "app:error_boundary_caught": {
    boundary?: string;
    error_message: string;
    component_stack?: string;
  };
  /** Auto-classification of the open document (runs once per document open). */
  "app:document_classified": {
    contract_type: string;
    confidence?: number;
    recommended_agent_id?: string;
  };
  "app:document_classification_failed": {
    error_code?: string;
    error_message?: string;
  };

  // ── auth: sign-in cascade, refresh, edge cases ───────────────────────────
  "auth:session_started": {
    detected_tier: string;
    host: string;
    platform: string;
  };
  "auth:session_completed": {
    detected_tier: string;
    final_tier: string;
    success: boolean;
    duration_ms: number;
  };
  "auth:naa_fallback": {
    reason: string;
    from_tier: "naa";
    to_tier: "legacy_sso";
    error_message?: string;
    correlation_id?: string;
    msal_error_code?: string;
    msal_sub_error?: string;
    msal_error_name?: string;
    /**
     * Sent explicitly rather than relying on the tenant_id super property:
     * that one is registered on a token write, so it is absent on the very
     * first NAA failure of a new install - exactly the event we need to
     * attribute when counting tenants that still lack admin consent.
     */
    tenant_id?: string;
  };
  "auth:token_refreshed": {
    trigger: TokenRefreshTrigger;
    auth_tier: string;
    /** True when the silent refresh failed but a full re-auth recovered it. */
    via_full_reauth?: boolean;
  };
  "auth:token_refresh_failed": {
    trigger: TokenRefreshTrigger;
    auth_tier: string;
    error_message?: string;
  };
  "auth:account_switched": Record<string, never>;
  "auth:unsupported_platform": { host: string; platform: string };
  /** Legacy SSO exhausted all retries; app continues in degraded mode. */
  "auth:degraded": {
    error_message: string;
    error_code?: number;
    total_attempts: number;
    auth_tier: "none";
  };
  /** MSAL's own error-level log line (v5 obfuscated code) - regression canary. */
  "auth:msal_internal_error": { message: string };
  /** Backend answered 401/403 to any API call. */
  "auth:rejected_by_backend": { status: number; endpoint: string };

  // ── session: stored conversations, restored on launch ────────────────────
  /** A stored conversation was read back and installed on launch. */
  "session:restored": {
    agent_id: string;
    message_count: number;
    /** How old the stored conversation was, in hours. */
    age_hours: number;
  };
  /**
   * A stored conversation could not be read, so saving is switched off for that
   * agent for the rest of the session.
   *
   * This is the one worth alerting on. Persistence is silent by design, so a
   * systemic read failure leaves users quietly without the feature and without
   * any other signal that it stopped working.
   */
  "session:restore_failed": {
    agent_id: string;
    /** Why the read did not conclude, e.g. "read_failed". */
    reason: string;
  };
  /** A save never reached the backend and its retry budget ran out. */
  "session:save_failed": { agent_id: string; attempts: number };
  /** The backend refused a save outright; resending it unchanged cannot help. */
  "session:save_rejected": { agent_id: string; http_status: number };
  /** Oldest turns were dropped to keep a conversation inside its size limit. */
  "session:payload_trimmed": { dropped: number; remaining: number };

  // ── attachments ──────────────────────────────────────────────────────────
  /**
   * Files were attached to the composer, counted after dedup and validation so
   * it reflects what the user actually ended up with.
   *
   * Fired at attach time rather than send time on purpose: `chat:message_sent`
   * already reports what rode along with a request, but that misses files a
   * user attaches and then abandons, which is exactly the signal needed to
   * judge how much the feature is reached for.
   */
  "attachment:added": {
    /** Which affordance was used - drag and drop, or the plus menu. */
    source: string;
    file_count: number;
    /** Lowercased extensions, no dot. */
    file_types: string[];
    total_bytes: number;
    /** Files silently ignored because they were already attached. */
    duplicates_skipped: number;
  };

  // ── chat: the send/response lifecycle ────────────────────────────────────
  "chat:message_sent": {
    agent_id: string;
    message_length: number;
    /** 1-based position of this user message in the conversation. */
    message_index: number;
    extended_thinking_enabled: boolean;
    web_search_enabled: boolean;
    concise_mode_enabled: boolean;
    composer_mode_enabled: boolean;
    benchmark_mode_enabled: boolean;
    track_changes_mode: string;
    /**
     * How Word is DISPLAYING tracked changes: "Balloon", "Inline", "Mixed", or
     * "unknown" where the host cannot report it.
     *
     * A separate axis from track_changes_mode, and the one that decides whether
     * tracked deletions are in the text layer the redline searches. Reported so
     * the size of the balloons population is answerable, which it otherwise is
     * not: "unknown" here is also the population that cannot benefit from the
     * display-mode handling at all.
     */
    markup_mode: string;
    attachment_count: number;
    attachment_types?: string[];
    attachment_bytes_total?: number;
    selected_playbook_id?: string;
    /**
     * Whether this turn came from the Quick Analysis action rather than from
     * something the user typed. The backend serves that turn differently - no
     * tools, playbook pre-loaded - so it is the cut that separates the two
     * routes in every other measure here.
     */
    quick_analysis: boolean;
    /**
     * Whether this turn was the Draft Certificate action: the Tier 3 final
     * eligibility validation, sent as a message from a card rather than typed.
     * Re-runs after document changes are the workflow, so counting these per
     * conversation is how many iterations a certificate took.
     */
    draft_certificate: boolean;
  };
  /** A certificate answer was downloaded as a Word document. */
  "certificate:exported": {
    success: boolean;
    /**
     * Whether the answer's header line was found, so the file could start
     * there. False means the export carried the whole answer, stray opening
     * text included.
     */
    header_found: boolean;
  };
  "chat:response_completed": {
    agent_id: string;
    response_length: number;
    thinking_used: boolean;
    web_search_count: number;
    web_fetch_count: number;
    tool_names?: string[];
    redline_suggestion_count?: number;
    stop_reason: string;
    /**
     * The backend's PostHog LLM-analytics trace for this turn, so this event
     * can be opened as a trace (what the model was given, how long it thought,
     * which tools it called, what it cost). Absent when the backend has LLM
     * analytics off, or on a build that predates it.
     */
    ai_trace_id?: string;
    /**
     * Wall-clock for the whole turn, measured on the client: from the moment
     * the send handler starts (before the document is read out of Word) to the
     * finished, rendered answer. Deliberately not the backend's own latency,
     * which starts only once the request reaches it - the difference is the
     * document read, upload and render the user also waited through.
     */
    total_ms: number;
    /** Send to the first chunk the user could see (thinking counts). */
    first_chunk_ms?: number;
  };
  "chat:response_failed": {
    agent_id: string;
    error_code: string;
    status_code?: number;
    received_chars?: number;
    ai_trace_id?: string;
    total_ms?: number;
  };
  /** Stream ended mid-answer without a normal completion or error callback. */
  "chat:response_interrupted": {
    agent_id: string;
    received_chars: number;
    ai_trace_id?: string;
    total_ms?: number;
    first_chunk_ms?: number;
  };
  "chat:response_truncated": {
    agent_id: string;
    stop_reason: "max_tokens";
    response_length: number;
    concise_mode_enabled: boolean;
    extended_thinking_enabled: boolean;
    web_search_enabled: boolean;
  };
  "chat:context_window_overflow": {
    agent_id: string;
    session_message_count: number;
    session_web_fetch_count: number;
    session_web_search_count: number;
    web_tools_used: boolean;
    error_code: string;
  };
  /** History rebuild dropped unsigned/empty thinking blocks (diagnostic). */
  "chat:history_thinking_dropped": {
    message_id: string;
    dropped_thinking_blocks: number;
    dropped_no_signature: number;
    dropped_no_content: number;
    total_thinking_blocks: number;
    kept_thinking_blocks: number;
  };
  "chat:new_conversation": { agent_id: string };
  "chat:agent_switched": {
    from_agent_id: string;
    from_agent_name: string;
    to_agent_id: string;
    to_agent_name: string;
  };
  "chat:files_attach_clicked": {
    agent_id: string;
    supported_types: string[];
    max_file_count?: number;
  };
  "chat:files_attached": {
    count: number;
    file_types: string[];
    file_bytes: number[];
  };

  // ── settings: one event for every toggle ─────────────────────────────────
  "settings:toggled": {
    setting: ChatSettingName;
    enabled: boolean;
    source?: "settings_menu" | "badge";
    during_streaming?: boolean;
  };

  // ── analysis: the retired Analysis tab (Analyze button + streamed summary).
  // @deprecated None of these are emitted any more - the tab was removed after
  // six months of production data showed a median visit of 3 seconds and only
  // 34 summaries actually run. The definitions stay so the historical insights
  // keep a matching schema. ──────────────────────────────────────────────────
  "analysis:started": {
    /** Length of the document OOXML string (characters, not bytes). */
    doc_chars?: number;
    track_changes_mode?: string;
  };
  "analysis:summary_completed": { summary_length: number };
  "analysis:summary_failed": {
    error_code?: string;
    error_message?: string;
    received_chars?: number;
  };
  "analysis:summary_retried": Record<string, never>;

  // ── dashboard: the retired Dashboard tab's data (structure + risk
  // distribution). @deprecated None of these are emitted any more. The tab was
  // removed, and with it the eager load at app open that fired its two backend
  // calls on every launch for a tab opened in 3% of sessions. The definitions
  // stay so the historical insights keep a matching schema. ──────────────────
  "dashboard:data_loaded": {
    section_count?: number;
    high_risk_count?: number;
    medium_risk_count?: number;
    low_risk_count?: number;
  };
  "dashboard:load_failed": {
    part: "structure" | "risk_chart";
    error_code?: string;
    error_message?: string;
  };
  "dashboard:risk_category_clicked": { category: string };

  // ── redline: auto-redline suggestions and their application ─────────────
  "redline:suggestions_received": {
    suggestion_count: number;
    composer_mode: boolean;
  };
  /** One suggestion card's Apply ran (manually or by composer auto-apply). */
  "redline:apply_clicked": {
    mode: "apply_all" | "composer_auto";
    edit_count: number;
  };
  "redline:apply_completed": {
    mode: "apply_all" | "composer_auto";
    applied_count: number;
    failed_count: number;
    /**
     * The stale guard accepted a changed paragraph because the change was one of
     * our own edits earlier in the same message, rather than the user's. Carried
     * on this event rather than its own so the outcome is on the same row — the
     * question is not just how often the escape hatch fires, but whether the
     * applies it lets through fail more often than normal ones.
     */
    self_attributed?: boolean;
  };
  /**
   * The stale-paragraph guard refused an apply (document changed under us).
   * Deliberately carries NO section name - section headings are contract
   * content, and document content never goes to analytics. paragraph_id is
   * enough to join against the backend's tool_use_results fingerprint.
   */
  "redline:apply_blocked_stale": {
    source: "preview" | "comment_card";
    paragraph_id?: string;
  };

  // ── citation / feedback ──────────────────────────────────────────────────
  "citation:tooltip_viewed": {
    citation_type: "playbook" | "catylex" | "document";
    document_title?: string;
    breadcrumb?: string;
    source_count?: number;
    chunk_type?: string;
    concept_count?: number;
    /** Set when the chip merged several adjacent citations: their count, and which one was viewed (0-based). */
    group_size?: number;
    group_index?: number;
  };
  "feedback:submitted": {
    rating: "positive" | "negative";
    agent_id?: string;
    has_comment?: boolean;
  };

  // ── api: deliberate backend rejections (4xx) ─────────────────────────────
  // Emitted by captureError.ts with the same dynamic property bag it attaches
  // to exceptions (error_code, api_name, status_code, network diagnostics,
  // call-site extras), so it stays a loose record by design.
  "api:request_rejected": Record<string, unknown>;
}

export type AnalyticsEventName = keyof AnalyticsEvents;

/**
 * Ambient context merged into every tracked event. Pushed by the owning
 * providers via `setAnalyticsContext` (module-level, like authRefreshBridge,
 * so non-React code paths get it too). Explicit event properties win on key
 * collisions.
 */
interface AmbientContext {
  agent_id?: string;
  /**
   * Always "chat" now that the tab strip is gone. Kept on every event so the
   * property does not disappear mid-series in PostHog.
   */
  active_view?: AnalyticsView;
}

let ambientContext: AmbientContext = {};

export const setAnalyticsContext = (patch: Partial<AmbientContext>): void => {
  ambientContext = { ...ambientContext, ...patch };
};

/** Test-only: reset module state between tests. */
export const resetAnalyticsContextForTests = (): void => {
  ambientContext = {};
};

/**
 * Capture a product event. Telemetry must never break the app, so failures
 * are swallowed (PostHog may be uninitialized when REACT_APP_POSTHOG_ON is
 * off - posthog-js no-ops with a console warning in that case).
 */
export const track = <E extends AnalyticsEventName>(event: E, props: AnalyticsEvents[E]): void => {
  try {
    posthog.capture(event, { ...ambientContext, ...props });
  } catch {
    /* telemetry failure is never fatal */
  }
};

/**
 * Identify the user (PII lives ONLY in the identify call, never in event
 * payloads - a PostHog filter rule must not be able to leak emails through
 * events).
 */
export const identifyUser = (email: string, properties: { name: string; email: string }): void => {
  try {
    posthog.identify(email, properties);
  } catch {
    /* telemetry failure is never fatal */
  }
};

/**
 * Register super properties (attached by posthog-js to every subsequent
 * event). Used for context that is expensive/impossible to thread per event:
 * auth_tier, tenant_id (from the JWT), Office host/platform.
 */
export const registerSuperProperties = (props: Record<string, unknown>): void => {
  try {
    posthog.register(props);
  } catch {
    /* telemetry failure is never fatal */
  }
};
